import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Chip
} from '@mui/material';

interface QualityAssessment {
  id?: number;
  call_id?: number;
  audio_url?: string | null;
  transcript?: string | null;
  model_name?: string | null;
  sale_probability: number;
  sale_probability_explanation: string;
  conversation_outcome: string;
  conversation_summary: string;
  client_mood_score: number;
  client_mood_explanation: string;
  client_facts: string[];
  client_needs: string;
  client_objections: string;
  manager_politeness_score: number;
  manager_politeness_explanation: string;
  manager_presentation: string;
  objection_handling_score: number;
  objection_handling_explanation: string;
  created_at?: string;
}

interface QualityAssessmentModalProps {
  open: boolean;
  onClose: () => void;
  assessment: QualityAssessment | null;
  leadInfo?: {
    phone?: string;
    customer_name?: string;
    date?: string;
  };
}

export const QualityAssessmentModal: React.FC<QualityAssessmentModalProps> = ({
  open,
  onClose,
  assessment,
  leadInfo
}) => {
  if (!assessment) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Оценка качества</DialogTitle>
        <DialogContent>
          <Typography>Оценка не найдена</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const getScoreColor = (score: number) => {
    if (score >= 60) return 'success';
    if (score >= 30) return 'warning';
    return 'default';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Оценка качества звонка
        {leadInfo && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {leadInfo.customer_name} • {leadInfo.phone} • {leadInfo.date && new Date(leadInfo.date).toLocaleDateString()}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Основные показатели */}
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Основные показатели</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Вероятность продажи</Typography>
                    <Chip 
                      label={`${assessment.sale_probability}%`} 
                      color={getScoreColor(assessment.sale_probability) as any}
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Настроение клиента</Typography>
                    <Chip 
                      label={`${assessment.client_mood_score}`} 
                      color={getScoreColor(assessment.client_mood_score) as any}
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Вежливость менеджера</Typography>
                    <Chip 
                      label={`${assessment.manager_politeness_score}`} 
                      color={getScoreColor(assessment.manager_politeness_score) as any}
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Отработка возражений</Typography>
                    <Chip 
                      label={`${assessment.objection_handling_score}`} 
                      color={getScoreColor(assessment.objection_handling_score) as any}
                      size="small"
                    />
                  </Box>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">Итог разговора</Typography>
                  <Typography variant="body1">{assessment.conversation_outcome || '—'}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Краткое содержание */}
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Краткое содержание</Typography>
                <Typography variant="body2" whiteSpace="pre-wrap">
                  {assessment.conversation_summary || '—'}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Двухколоночная сетка для остальных карточек */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {/* Ключевые факты о клиенте */}
            <Box>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Ключевые факты о клиенте</Typography>
                  {assessment.client_facts?.length ? (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {assessment.client_facts.map((fact, i) => (
                        <li key={i}>
                          <Typography variant="body2">{fact}</Typography>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Typography variant="body2">—</Typography>
                  )}
                </CardContent>
              </Card>
            </Box>

            {/* Потребности клиента */}
            <Box>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Потребности клиента</Typography>
                  <Typography variant="body2" whiteSpace="pre-wrap">
                    {assessment.client_needs || '—'}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Возражения клиента */}
            <Box>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Возражения клиента</Typography>
                  <Typography variant="body2" whiteSpace="pre-wrap">
                    {assessment.client_objections || '—'}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Объяснение вероятности продажи */}
            <Box>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Объяснение вероятности продажи</Typography>
                  <Typography variant="body2" whiteSpace="pre-wrap">
                    {assessment.sale_probability_explanation || '—'}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Вежливость менеджера */}
            <Box>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Вежливость менеджера</Typography>
                  <Typography variant="body2" whiteSpace="pre-wrap">
                    {assessment.manager_politeness_explanation || '—'}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Отработка возражений */}
            <Box>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Отработка возражений</Typography>
                  <Typography variant="body2" whiteSpace="pre-wrap">
                    {assessment.objection_handling_explanation || '—'}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Техническая информация */}
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Техническая информация</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Модель</Typography>
                    <Typography variant="body2">{assessment.model_name || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Дата оценки</Typography>
                    <Typography variant="body2">
                      {assessment.created_at ? new Date(assessment.created_at).toLocaleString() : '—'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
}; 